import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import axios from "axios";

const PageContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 40px 20px;
`;

const Header = styled.header`
  margin-bottom: 30px;
`;

const Title = styled.h1`
  font-size: 28px;
  color: #333;
  margin-bottom: 10px;
`;

const Subtitle = styled.p`
  font-size: 16px;
  color: #666;
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
  color: #333;
`;

const Input = styled.input`
  width: 100%;
  padding: 10px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
`;

const Textarea = styled.textarea`
  width: 100%;
  padding: 10px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  min-height: 100px;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 30px;
`;

const Button = styled.button`
  padding: 10px 15px;
  border: none;
  border-radius: 4px;
  font-size: 14px;
  cursor: pointer;
`;

const CancelButton = styled(Button)`
  background-color: #f5f5f5;
  color: #333;

  &:hover {
    background-color: #e0e0e0;
  }
`;

const SubmitButton = styled(Button)`
  background-color: #4285f4;
  color: white;

  &:hover {
    background-color: #3367d6;
  }

  &:disabled {
    background-color: #cccccc;
    cursor: not-allowed;
  }
`;

const ErrorMessage = styled.div`
  color: #d93025;
  margin-top: 20px;
  padding: 10px;
  background-color: #fce8e6;
  border-radius: 4px;
`;

const SuccessMessage = styled.div`
  color: #137333;
  margin-top: 20px;
  padding: 10px;
  background-color: #e6f4ea;
  border-radius: 4px;
`;



const SectionTitle = styled.h3`
  font-size: 18px;
  color: #333;
  margin: 30px 0 15px;
  padding-bottom: 8px;
  border-bottom: 1px solid #eee;
`;

function CreateSmsEmailReportPage() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [formData, setFormData] = useState({
    campaignName: "",
    reportDate: new Date().toISOString().split("T")[0],
    smsContent: "",
    emailHtml: "",
  });
  const [emailContentType, setEmailContentType] = useState("html");
  const [emailImage, setEmailImage] = useState(null);

  const [smsData, setSmsData] = useState({
    sent: "",
    delivered: "",
    previews: "",
    clicks: "",
    unsubscribes: "",
    cost: ""
  });
  const [emailData, setEmailData] = useState({
    sent: "",
    delivered: "",
    opens: "",
    clicks: "",
    unsubscribes: "",
    cost: ""
  });

  const navigate = useNavigate();
  const apiUrl =
    process.env.REACT_APP_API_URL || "https://report-campaign.sirdata.io";

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSmsDataChange = (e) => {
    const { name, value } = e.target;
    setSmsData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleEmailDataChange = (e) => {
    const { name, value } = e.target;
    setEmailData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const compressImage = (file, maxWidth = 400, quality = 0.5) => {
    return new Promise((resolve) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();
      
      img.onload = () => {
        const ratio = Math.min(maxWidth / img.width, maxWidth / img.height);
        canvas.width = img.width * ratio;
        canvas.height = img.height * ratio;
        
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        
        // Compression plus agressive
        let compressedData = canvas.toDataURL('image/jpeg', quality);
        
        // Si encore trop gros, réduire davantage
        if (compressedData.length > 500000) { // ~500KB en base64
          const smallerRatio = Math.min(300 / img.width, 300 / img.height);
          canvas.width = img.width * smallerRatio;
          canvas.height = img.height * smallerRatio;
          ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
          compressedData = canvas.toDataURL('image/jpeg', 0.3);
        }
        
        resolve(compressedData);
      };
      
      img.src = URL.createObjectURL(file);
    });
  };

  const handleEmailImageUpload = async (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        setError("L'image est trop volumineuse (max 10MB)");
        return;
      }
      
      try {
        const compressedImage = await compressImage(file);
        setEmailImage(compressedImage);
        setError("");
      } catch (err) {
        setError("Erreur lors du traitement de l'image");
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setSuccess("");

    const hasSmsData = Object.values(smsData).some(value => value.trim() !== "");
    const hasEmailData = Object.values(emailData).some(value => value.trim() !== "");
    
    if (!hasSmsData && !hasEmailData) {
      setError(
        "Veuillez remplir au moins les données SMS ou Email"
      );
      setLoading(false);
      return;
    }

    try {
      const reportData = {
        ...formData,
        smsData,
        emailData,
        emailImage: emailContentType === "image" ? emailImage : null,
      };

      const response = await axios.post(
        `${apiUrl}/api/reports/create-sms-email`,
        reportData
      );

      if (response.data.success) {
        setSuccess("Rapport créé avec succès!");
        setTimeout(() => {
          navigate(`/dashboard/${response.data.reportId}`);
        }, 1500);
      } else {
        setError(
          response.data.message || "Erreur lors de la création du rapport"
        );
      }
    } catch (err) {
      setError(`Erreur lors de la création du rapport: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate("/reports");
  };

  return (
    <PageContainer>
      <Header>
        <Title>Créer un rapport SMS/Email</Title>
        <Subtitle>
          Générez un rapport combiné pour vos campagnes SMS et Email
        </Subtitle>
      </Header>

      <form onSubmit={handleSubmit}>
        <FormGroup>
          <Label>Nom de la campagne</Label>
          <Input
            type="text"
            name="campaignName"
            value={formData.campaignName}
            onChange={handleChange}
            placeholder="Nom de la campagne"
            required
          />
        </FormGroup>

        <FormGroup>
          <Label>Date du rapport</Label>
          <Input
            type="date"
            name="reportDate"
            value={formData.reportDate}
            onChange={handleChange}
            required
          />
        </FormGroup>

        <SectionTitle>Données SMS</SectionTitle>

        <FormGroup>
          <Label>Nombre de SMS envoyés</Label>
          <Input
            type="number"
            name="sent"
            value={smsData.sent}
            onChange={handleSmsDataChange}
            placeholder="Ex: 10000"
          />
        </FormGroup>

        <FormGroup>
          <Label>Nombre de SMS délivrés</Label>
          <Input
            type="number"
            name="delivered"
            value={smsData.delivered}
            onChange={handleSmsDataChange}
            placeholder="Ex: 9500"
          />
        </FormGroup>

        <FormGroup>
          <Label>Nombre de previews</Label>
          <Input
            type="number"
            name="previews"
            value={smsData.previews}
            onChange={handleSmsDataChange}
            placeholder="Ex: 8000"
          />
        </FormGroup>

        <FormGroup>
          <Label>Nombre de clics</Label>
          <Input
            type="number"
            name="clicks"
            value={smsData.clicks}
            onChange={handleSmsDataChange}
            placeholder="Ex: 150"
          />
        </FormGroup>

        <FormGroup>
          <Label>Nombre de désabonnements</Label>
          <Input
            type="number"
            name="unsubscribes"
            value={smsData.unsubscribes}
            onChange={handleSmsDataChange}
            placeholder="Ex: 25"
          />
        </FormGroup>

        <FormGroup>
          <Label>Coût total (€)</Label>
          <Input
            type="number"
            step="0.01"
            name="cost"
            value={smsData.cost}
            onChange={handleSmsDataChange}
            placeholder="Ex: 500.00"
          />
        </FormGroup>

        <FormGroup>
          <Label>Contenu du SMS</Label>
          <Textarea
            name="smsContent"
            value={formData.smsContent}
            onChange={handleChange}
            placeholder="Entrez le contenu du SMS envoyé"
          />
        </FormGroup>

        <SectionTitle>Données Email</SectionTitle>

        <FormGroup>
          <Label>Nombre d'emails envoyés</Label>
          <Input
            type="number"
            name="sent"
            value={emailData.sent}
            onChange={handleEmailDataChange}
            placeholder="Ex: 50000"
          />
        </FormGroup>

        <FormGroup>
          <Label>Nombre d'emails délivrés</Label>
          <Input
            type="number"
            name="delivered"
            value={emailData.delivered}
            onChange={handleEmailDataChange}
            placeholder="Ex: 48000"
          />
        </FormGroup>

        <FormGroup>
          <Label>Nombre d'ouvertures</Label>
          <Input
            type="number"
            name="opens"
            value={emailData.opens}
            onChange={handleEmailDataChange}
            placeholder="Ex: 12000"
          />
        </FormGroup>

        <FormGroup>
          <Label>Nombre de clics</Label>
          <Input
            type="number"
            name="clicks"
            value={emailData.clicks}
            onChange={handleEmailDataChange}
            placeholder="Ex: 2400"
          />
        </FormGroup>

        <FormGroup>
          <Label>Nombre de désabonnements</Label>
          <Input
            type="number"
            name="unsubscribes"
            value={emailData.unsubscribes}
            onChange={handleEmailDataChange}
            placeholder="Ex: 120"
          />
        </FormGroup>

        <FormGroup>
          <Label>Coût total (€)</Label>
          <Input
            type="number"
            step="0.01"
            name="cost"
            value={emailData.cost}
            onChange={handleEmailDataChange}
            placeholder="Ex: 1200.00"
          />
        </FormGroup>

        <FormGroup>
          <Label>Type de contenu Email</Label>
          <select
            value={emailContentType}
            onChange={(e) => setEmailContentType(e.target.value)}
            style={{
              width: "100%",
              padding: "10px",
              border: "1px solid #ddd",
              borderRadius: "4px",
              fontSize: "14px"
            }}
          >
            <option value="html">Code HTML</option>
            <option value="image">Image</option>
          </select>
        </FormGroup>

        {emailContentType === "html" ? (
          <FormGroup>
            <Label>Kit HTML de l'Email</Label>
            <Textarea
              name="emailHtml"
              value={formData.emailHtml}
              onChange={handleChange}
              placeholder="Collez ici le code HTML de l'email"
            />
          </FormGroup>
        ) : (
          <FormGroup>
            <Label>Image de l'Email</Label>
            <input
              type="file"
              accept="image/jpeg,image/jpg,image/png,image/webp"
              onChange={handleEmailImageUpload}
              style={{
                width: "100%",
                padding: "10px",
                border: "1px solid #ddd",
                borderRadius: "4px",
                fontSize: "14px"
              }}
            />
            <div style={{ fontSize: "12px", color: "#666", marginTop: "5px" }}>
              Formats acceptés: JPG, PNG, WebP (max 10MB). L'image sera automatiquement compressée à 400px max.
            </div>
            {emailImage && (
              <div style={{ marginTop: "10px" }}>
                <p>Aperçu :</p>
                <img
                  src={emailImage}
                  alt="Aperçu email"
                  style={{
                    maxWidth: "100%",
                    maxHeight: "300px",
                    border: "1px solid #ddd",
                    borderRadius: "4px"
                  }}
                />
              </div>
            )}
          </FormGroup>
        )}

        {error && <ErrorMessage>{error}</ErrorMessage>}
        {success && <SuccessMessage>{success}</SuccessMessage>}

        <ButtonGroup>
          <CancelButton type="button" onClick={handleCancel}>
            Annuler
          </CancelButton>
          <SubmitButton type="submit" disabled={loading}>
            {loading ? "Création en cours..." : "Créer le rapport"}
          </SubmitButton>
        </ButtonGroup>
      </form>
    </PageContainer>
  );
}

export default CreateSmsEmailReportPage;
